import React, { FC } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { TransitionProps } from '@mui/material/transitions';
import Slide from '@mui/material/Slide';
import useSnackbar from '../../../../../hooks/useSnackbar';
import {
  FastAPIPublishKIDPayload,
  usePublishDocumentMutation,
} from '../../mutations';
import TokenService from '../../../../../services/token-service';
import { useQueryClient } from '@tanstack/react-query';

interface PublishDocumentDialogBoxProps {
  setDialogIsShowing: (value: boolean) => void;
  fundId: string;
  shareClass: string;
  documentLanguage: string;
  fundName: string;
  fundUUID: string;
  updateVersionNumber: (
    fundId: string,
    shareClass: string,
    documentLanguage: string,
  ) => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PublishDocumentDialogBox: FC<PublishDocumentDialogBoxProps> = (props) => {
  const {
    setDialogIsShowing,
    fundId,
    shareClass,
    documentLanguage,
    fundName,
    updateVersionNumber,
    fundUUID,
  } = props;
  const { mutateAsync, isPending, isSuccess, error } =
    usePublishDocumentMutation();
  const { user_id, user_name, user_client_id } = TokenService.getUser()!;
  const { showAsyncSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  async function publishDocument() {
    const payload: FastAPIPublishKIDPayload = {
      client_id: user_client_id,
      fund_id: fundUUID,
      fund_id_string: fundId,
      share_class: shareClass,
      document_language: documentLanguage,
      user_id,
      user_name,
    };

    await showAsyncSnackbar({
      startMessage: 'Publishing Latest Document.',
      successMessage: 'Document Published.',
      failureMessage: 'An error occurred while publishing.',
      promiseToResolve: mutateAsync(payload).then(() => {
        updateVersionNumber(fundId, shareClass, documentLanguage);
        queryClient.invalidateQueries({
          queryKey: ['kids-published-figures', user_client_id],
        });

        // 2. Remove cached data so placeholderData won't trigger
        queryClient.removeQueries({
          queryKey: ['kids-published-figures', user_client_id],
        });

        queryClient.invalidateQueries({
          queryKey: ['kids-overview-data'],
        });

        setDialogIsShowing(false);
      }),
    });
  }

  return (
    <Dialog
      open={true}
      TransitionComponent={Transition}
      keepMounted
      aria-describedby="alert-dialog-slide-description"
      maxWidth="md"
      fullWidth={true}
      PaperProps={{ style: { overflowY: 'visible' } }}
    >
      <DialogTitle id="publish-header-text" textAlign="center">
        Publish Document?
      </DialogTitle>
      <DialogContentText id="publish-subheader-text" textAlign="center">
        Update the version number of this document to indicate the current
        version is published.
      </DialogContentText>
      <DialogContentText
        id="publish-fund-text"
        textAlign="center"
      >{`${fundName} - ${shareClass}`}</DialogContentText>
      <DialogActions>
        <Button onClick={() => setDialogIsShowing(false)}>Cancel</Button>
        <Button onClick={publishDocument}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishDocumentDialogBox;
